/**
 * @fileoverview A modal component for selecting application language.
 *
 * This component provides a user interface for changing the application's
 * language. It displays a list of available languages with search functionality,
 * highlighting top languages at the top and providing a searchable grid of all
 * available languages below. The modal supports both light and dark themes.
 */

import React, { useState, useRef, useEffect } from "react";
import { Globe, Search } from "lucide-react";
import { useLanguage } from "../lib/LanguageContext";
import { useTranslation } from "react-i18next";

/**
 * Top languages that are prominently displayed at the top of the modal.
 * These are the most commonly used languages and are shown as quick-access buttons.
 */
const TOP_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "fr", name: "French", nativeName: "Français" },
];

/**
 * Complete list of all available languages in the application.
 * This includes the 11 initially supported languages.
 */
const ALL_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
];

/**
 * Props for the LanguageSelectorModal component.
 */
type Props = {
  /** Flag indicating whether the modal is open and visible. */
  isOpen: boolean;
  /** Callback function to close the modal. */
  onClose: () => void;
  /** Callback function called when a language is selected, with the language code as parameter. */
  onSelect: (langCode: string) => void;
};

/**
 * A modal component for selecting the application language.
 * 
 * This component renders a modal dialog that allows users to change the
 * application's language. It features a prominent display of top languages
 * (English, Spanish, French) as quick-access buttons, followed by a searchable
 * grid of all available languages. The modal includes search functionality
 * that filters languages by both English name and native name. It supports
 * both light and dark themes and includes click-outside-to-close functionality.
 * 
 * @param {Props} props - The props for the language selector modal.
 * @returns {JSX.Element | null} The rendered modal component or null if not open.
 */
export const LanguageSelectorModal: React.FC<Props> = ({ isOpen, onClose, onSelect }) => {
  const [search, setSearch] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const { languages: allLanguages, currentLanguage } = useLanguage();
  const { t, i18n } = useTranslation();

  // Effect to handle clicking outside the modal to close it
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose]);

  // Filter languages based on search input (matches both English and native names)
  const filteredLanguages = allLanguages.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.nativeName.toLowerCase().includes(search.toLowerCase())
  );

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('LanguageSelectorModal: Modal opened');
      console.log('LanguageSelectorModal: Current language from context:', currentLanguage);
      console.log('LanguageSelectorModal: i18n.language:', i18n.language);
      console.log('LanguageSelectorModal: Available languages:', allLanguages.length);
    }
  }, [isOpen, currentLanguage, i18n.language, allLanguages.length]);

  const handleLanguageSelect = (langCode: string) => {
    console.log('LanguageSelectorModal: Language selected:', langCode);
    console.log('LanguageSelectorModal: Current i18n language before change:', i18n.language);
    onSelect(langCode);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-2 p-0 flex flex-col"
      >
        {/* Modal title with globe icon */}
        <div className="flex items-center gap-2 px-6 py-4 border-b dark:border-gray-700 rounded-t-xl">
          <Globe className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          <span className="font-semibold text-lg dark:text-white">{t('common.language')}</span>
        </div>

        {/* Top languages section with quick-access buttons */}
        <div className="px-6 pt-4">
          <div className="text-sm font-medium mb-2 dark:text-gray-300">{t('language.topLanguages')}</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {TOP_LANGUAGES.map(lang => (
              <button
                key={lang.code}
                className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium focus:outline-none transition-colors"
                onClick={() => handleLanguageSelect(lang.code)}
              >
                {lang.nativeName}
              </button>
            ))}
          </div>
        </div>

        {/* All languages section with search functionality */}
        <div className="px-6 pb-4">
          <div className="text-sm font-medium mb-2 dark:text-gray-300">{t('language.allLanguages')}</div>
          {/* Search input with search icon */}
          <div className="relative mb-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Search className="w-4 h-4" />
            </span>
            <input
              className="w-full pl-9 pr-3 py-2 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 text-sm dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              placeholder={t('language.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {/* Scrollable grid of all available languages */}
          <div className="max-h-48 overflow-y-auto grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {filteredLanguages.map(lang => (
              <button
                key={lang.code}
                className="text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md px-2 py-1.5 dark:text-gray-200 transition-colors"
                onClick={() => handleLanguageSelect(lang.code)}
              >
                <div className="font-medium">{lang.nativeName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{lang.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Close button at the bottom */}
        <div className="flex justify-end px-6 py-3 border-t dark:border-gray-700 rounded-b-xl">
          <button
            className="bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 px-4 py-2 rounded-md text-sm font-medium focus:outline-none transition-colors"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}; 